import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, BehaviorSubject, of, firstValueFrom } from 'rxjs';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { compactNavigation, defaultNavigation, futuristicNavigation, horizontalNavigation } from './navigation.data';
import { IMenu } from '@shared/models/menu';
import { environment } from 'environments/environment';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    navegacionMenu = [];
    inProcess = false;
    isMenuLoaded = false;
    roles: any[] = [];
    private _menuParsedSubject: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private _navigation: BehaviorSubject<Navigation> = new BehaviorSubject<Navigation>({
        compact: [],
        default: [],
        futuristic: [],
        horizontal: []
    });
    private readonly _compactNavigation: FuseNavigationItem[] = compactNavigation;
    // private readonly _defaultNavigation: FuseNavigationItem[] = defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] = futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] = horizontalNavigation;
    private _menuLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {
        // Inicializar la navegación con los menús predeterminados
        this._navigation.next({
            compact: this._compactNavigation,
            default: this.navegacionMenu,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        });
        // Recuperar roles del localStorage al iniciar
        this.roles = JSON.parse(localStorage.getItem('roles')) || [];
    }

    cantidadRoles(roles) {
        this.roles = roles;
        // Almacenar roles en localStorage
        localStorage.setItem('roles', JSON.stringify(this.roles));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter para la navegación
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Métodos públicos
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter para los datos del menú de navegación
     */
    set _navigationMenuData(menuData) {
        this.navegacionMenu = this.parsearMenuNavegacion(menuData, this.roles);
        // Actualizar la navegación con los nuevos datos del menú
        this._navigation.next({
            compact: this._compactNavigation,
            default: this.navegacionMenu,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        });
    }

    /**
     * Getter para los datos del menú de navegación
     */
    get _navigationMenuData() {

        return this.navegacionMenu;
    }

    /**
     * Esta función se utiliza para parsear los datos del menú de navegación
     * que devuelve el servicio backend.
     * @param menu 
     * @returns 
     */
    parsearMenuNavegacion(menu: IMenu[], roles: any[]) {
        let menuData = [];
        const roleCount = roles.length; // Contar el número de roles
    
        menu.forEach(i => {
            let hijos = this.tieneHijos(i.hijos);
    
            // Verificar si el item es "Inicio" y si el usuario tiene más de 2 roles
            if (i.item.descripcion === 'Inicio' && roleCount < 2) {
                return; // No agregar "Inicio" al menú
            }
    
            let link = (i.item.descripcion === 'Inicio') ? 'seleccion-roles' : i.item.ruta; // Asignar '/#' si es "Inicio"
    
            let item = {
                id: i.item.descripcion.toLowerCase().replace(' ', ''),
                title: i.item.descripcion,
                type: i.hijos.length > 0 ? 'collapsable' : 'basic',
                link: link, // Asignar la ruta o '/#'
                icon: i.item.icono ? i.item.icono : null,
                children: hijos
            }
            menuData.push(item);
        });
        this._menuParsedSubject.next(menuData);
        return menuData;
    }

    /**
    * Función recursiva que recorre los submenús para luego pintarlos.
    * @param hijos 
    */
    tieneHijos(hijos): any[] {
        const data = [];

        hijos.forEach(subItem => {
            // Recursión: si tiene hijos, obtenerlos
            const children = subItem.hijos.length > 0 ? this.tieneHijos(subItem.hijos) : [];

            const item = {
                id: subItem.item.descripcion.toLowerCase().replace(' ', ''),
                title: subItem.item.descripcion,
                type: subItem.hijos.length > 0 ? 'collapsable' : 'basic',
                link: subItem.item.ruta,
                icon: subItem.item.icono ? subItem.item.icono : null,
                children: children
            };

            data.push(item);
        });

        return data;
    }

    /**
     * Obtener los menús
     */
    async getMenus() {
        
        if (!this.inProcess && !this.isMenuLoaded) {
            try {
                const res = await this.getMenusX();
                this.inProcess = false;
                this.isMenuLoaded = res;
                this._menuLoadedSubject.next(res);
            } catch (error) {
                console.error('NavigationService: Error al cargar el menú:', error);
                this.inProcess = false;
                this._menuLoadedSubject.next(false);
            }
        } 
    }

    async getMenusX(): Promise<boolean> {
        this.inProcess = true;
        try {
            const res = await firstValueFrom(this._httpClient.post(`${environment.menu}`, null));
            this._navigationMenuData = res['result'];
            return true;
        } catch (error) {
            console.error('NavigationService: Error al obtener menús:', error);
            return false;
        } finally {
            this.inProcess = false;
        }
    }

    // }
    /**
     * Obtener todos los datos de navegación
     */
    get(): Observable<Navigation> {
        const navigationData = {
            compact: this._compactNavigation,
            default: this._navigationMenuData,
            futuristic: this._futuristicNavigation,
            horizontal: this._horizontalNavigation,
        }

        return of(navigationData);
    }
    
    get menuLoaded$(): Observable<boolean> {
        return this._menuLoadedSubject.asObservable();
    }

    get menuParsed$(): Observable<any[]> {
        return this._menuParsedSubject.asObservable();
    }

    // Metodo para obtener un cierto arreglo del item de menu
    getItemById(id: string): any {
        const items = this._menuParsedSubject.getValue();

        const buscar = (items: any[]): any => {
            for (const item of items) {
                if (item.id === id) {
                    return item;
                }

                if (item.children?.length > 0) {
                    const encontrado = buscar(item.children);
                    if (encontrado) {
                        return encontrado;
                    }
                }
            }

            return null;
        };

        return buscar(items);
    }

}
